import { z } from 'zod'

/**
 * Broad category of a furniture item, used to drive programmatic layouts (e.g.
 * seating charts pair Chairs to Tables). Two classes are not drawn: a dance
 * floor is a square whose sizes come from a default and a step, and a riser is
 * a runway of units whose size comes from one unit's width and length.
 * Mirrors the `FurnitureClass` enum in the Prisma schema — the two must stay
 * in sync. Single source of truth the web app imports; do not redeclare the
 * union anywhere else.
 */
export const FURNITURE_CLASSES = ['Chair', 'Table', 'DanceFloor', 'Riser', 'Other'] as const

export const furnitureClassSchema = z.enum(FURNITURE_CLASSES)

export type FurnitureClass = z.infer<typeof furnitureClassSchema>

const FURNITURE_CLASS_LABELS: Record<FurnitureClass, string> = {
  Chair: 'Chair',
  Table: 'Table',
  DanceFloor: 'Dance floor',
  Riser: 'Riser',
  Other: 'Other',
}

/** What a person sees for a class. The stored value stays the enum. */
export function furnitureClassLabel(furnitureClass: FurnitureClass): string {
  return FURNITURE_CLASS_LABELS[furnitureClass]
}

/**
 * Whether a piece of this class is drawn in the furniture editor. A dance floor
 * and a riser are not: their shape comes from their measurements, so the forms
 * ask for those instead of a drawing.
 */
export function isDrawnClass(furnitureClass: FurnitureClass): boolean {
  return furnitureClass !== 'DanceFloor' && furnitureClass !== 'Riser'
}
